import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { Observable } from "rxjs/Observable";
import { CanvasService } from "../../canvas/canvas-service.interface";

export class PainSiteService implements CanvasService {
  categoryObservable = new BehaviorSubject<string>("");
  backgroundObservable = new BehaviorSubject<string>("");
  drawPointsObservable = new BehaviorSubject<{ points: Array<object> }>({
    points: [],
  });
  allPointsObservable = new BehaviorSubject<{
    category: string;
    points: Array<object>;
  }>({ category: "", points: [] });
  activeIndex: number;
  currentCategory: string = "body";
  dataSet: Array<object> = [];

  constructor(public config: any, public translatePipe: TranslateService) {
    this.categoryObservable.next(this.currentCategory);
    this.pushBackgroundData();
  }

  addDefaultPoint(painScreeningValues: {
    category: string;
    painTool: string;
    index: number;
    active: boolean;
    unableToScore: any;
    painValues: object;
    severity: number;
  }) {
    this.dataSet.push(painScreeningValues);
    this.currentCategory = painScreeningValues.category;
    this.pushData();
  }

  //
  // Canvas service functions
  //

  getCategories() {
    return ["body", "head", "hands", "feet"]; //TODO should be retrieved from config?
  }

  setCategory(category: string) {
    if (category !== this.currentCategory) {
      this.currentCategory = category;
      this.categoryObservable.next(this.currentCategory);
      this.pushBackgroundData();
    }
  }

  getCategoryObservable(): Observable<string> {
    return this.categoryObservable.asObservable();
  }

  getBackgroundObservable(): Observable<string> {
    return this.backgroundObservable.asObservable();
  }

  getDrawPointsDataObservable(): Observable<{ points: Array<object> }> {
    return this.drawPointsObservable.asObservable();
  }

  addPointData(data: object) {
    if (data) {
      this.dataSet.push(data);
    }
    this.pushData();
    this.pushDrawData();
  }

  setAllPointData(data: Array<object>) {
    if (data) {
      this.dataSet = data;
    }
    this.pushData();
    this.pushDrawData();
  }

  getAllPointData() {
    return this.dataSet;
  }

  getAllPointDataObservable(): Observable<{
    category: string;
    points: Array<object>;
  }> {
    return this.allPointsObservable.asObservable();
  }

  addPoint(): void {
    this.dataSet.push({
      x: undefined,
      y: undefined,
      category: this.currentCategory,
    });

    this.pushData();
  }

  getPoint(index: number) {
    return this.dataSet[index];
  }

  removePoint(index: number): void {
    // update activeIndex if it is deleted or is after the deleted index
    if (index == this.activeIndex) {
      this.activeIndex = undefined;
    } else if (index < this.activeIndex) {
      this.activeIndex--;
    }
    this.dataSet.splice(index, 1);
    this.pushData();
    this.pushDrawData();
  }

  selectPoint(index: number): void {
    if (index === undefined || index === null) {
      return;
    }
    //change tab to selected
    this.setCategory(this.dataSet[index]["category"]);

    //unset current active
    if (this.activeIndex != undefined) {
      this.dataSet[this.activeIndex]["active"] = false;
    }

    //unset active state if it's already selected (toggle
    if (this.activeIndex == index) {
      this.activeIndex = undefined;
    } else {
      //set new active if it's not already selected
      this.activeIndex = index;
      this.dataSet[this.activeIndex]["active"] = true;
    }
    // if(this.dataSet[index]['active'] != null)
    // {
    //     if(this.dataSet[index]['active'] == false)
    //     {
    //         if(this.dataSet[index]['createDate'] == null)
    //         {
    //          this.dataSet[index]['name'] = null;
    //          this.clearDropDownsValue(index);
    //         }
    //     }
    // }

    this.pushData();
    this.pushDrawData();
  }

  updateActivePointData(key, value): boolean {
    let index = this.activeIndex;
    if (
      index === undefined ||
      index === null ||
      index >= this.dataSet.length ||
      key === undefined ||
      key === null
    ) {
      return false;
    }

    if (key === "unspecifiedLocation" && value === true) {
      this.dataSet[index]["name"] = null;
      this.dataSet[index]["x"] = null;
      this.dataSet[index]["y"] = null;
      this.clearDropDownsValue(index);
      this.pushDrawData();
      this.dataSet[index]["unspecifiedLocation"] = true;
    }

    if (key === "unableToDetermine" && value === true) {
      this.dataSet[index]["name"] = null;
      this.dataSet[index]["x"] = null;
      this.dataSet[index]["y"] = null;
      this.clearDropDownsValue(index);
      this.pushDrawData();
      this.dataSet[index]["unableToDetermine"] = true;
    }

    if (key === "name" && value != null) {
      this.dataSet[index]["unspecifiedLocation"] = false;
      this.clearDropDownsValue(index);
    }

    if (key === "unableToScore" && value === true) {
      this.dataSet[index]["painTool"] = null;
      this.dataSet[index]["severity"] = null;
    } else if ((key === "severity" || key === "painTool") && value !== null) {
      this.dataSet[index]["unableToScore"] = null;
    }

    this.dataSet[index][key] = value;
    this.pushData();
    return true;
  }

  clearDropDownsValue(index): void {
    //Clear Dropdowns in step 2
    if (this.dataSet[index]["type"]) this.dataSet[index]["type"] = null;
    if (this.dataSet[index]["character"])
      this.dataSet[index]["character"] = null;
    if (this.dataSet[index]["descriptiveSeverity"])
      this.dataSet[index]["descriptiveSeverity"] = null;
    if (this.dataSet[index]["duration"]) this.dataSet[index]["duration"] = null;
    if (this.dataSet[index]["frequency"])
      this.dataSet[index]["frequency"] = null;
    if (this.dataSet[index]["onset"]) this.dataSet[index]["onset"] = null;
  }

  updateActiveCoords(x: number, y: number): void {
    let activeIndex = this.activeIndex;
    if (activeIndex === undefined || activeIndex === null) {
      return;
    }

    this.dataSet[activeIndex]["category"] = this.currentCategory;
    this.dataSet[activeIndex]["x"] = x;
    this.dataSet[activeIndex]["y"] = y;

    if (this.dataSet[activeIndex]["unspecifiedLocation"]) {
      this.dataSet[activeIndex]["unspecifiedLocation"] = false;
      this.clearDropDownsValue(activeIndex);
    }

    this.pushDrawData();
  }

  //
  // Internal functions
  //
  pushDrawData() {
    let drawPoints = this.dataSet.filter(
      (point) => point["category"] === this.currentCategory
    );
    this.drawPointsObservable.next({ points: drawPoints });
  }

  pushData() {
    this.allPointsObservable.next({
      category: this.currentCategory,
      points: this.dataSet,
    });
  }

  pushBackgroundData() {
    if (this.config && this.config.backgrounds) {
      if (this.currentCategory == this.translatePipe.instant("painAssessed"))
        this.currentCategory = "body";
      if (this.currentCategory == this.translatePipe.instant("body"))
        this.currentCategory = "body";

      let bg = this.config.backgrounds[this.currentCategory];
      //Use relative path of asset images, remove all leading ../ from path
      let assetPathIndex = bg.lastIndexOf("/assets/svg/pain");
      if (assetPathIndex >= -1 && assetPathIndex < bg.length) {
        bg = bg.substr(assetPathIndex + 1);
      }
      this.backgroundObservable.next(bg);
      this.pushDrawData();
    }
  }

  clearAllPoints() {
    this.activeIndex = undefined;
    this.dataSet = [this.dataSet[0]]; //keep the first default point
    this.pushData();
    this.pushDrawData();
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/cards/pain-site-card/pain-site.service.ts